import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ArrowUpRight, ArrowDownLeft, Flame, Coins } from 'lucide-react';
import type { DfeEnrichedTransferEntry } from '@/lib/api';

interface TransferHistoryListProps {
  transfers: DfeEnrichedTransferEntry[];
  currentUserId: string;
  loading: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
}

function getTransferDisplay(
  entry: DfeEnrichedTransferEntry,
  currentUserId: string,
): {
  label: string;
  variant: 'default' | 'secondary' | 'destructive' | 'outline';
  icon: React.ReactNode;
  otherParty: string;
} {
  switch (entry.transferType) {
    case 'transfer': {
      const isSender = entry.fromUserId === currentUserId;
      return {
        label: isSender ? 'Sent' : 'Received',
        variant: isSender ? 'outline' : 'default',
        icon: isSender ? (
          <ArrowUpRight className="h-3 w-3" />
        ) : (
          <ArrowDownLeft className="h-3 w-3" />
        ),
        otherParty: isSender
          ? entry.toUsername || entry.toUserId || '-'
          : entry.fromUsername || entry.fromUserId || '-',
      };
    }
    case 'mint':
    case 'admin_grant':
    case 'legacy_import':
      return {
        label: 'Minted',
        variant: 'secondary',
        icon: <Coins className="h-3 w-3" />,
        otherParty: entry.fromUsername || entry.fromUserId || 'System',
      };
    case 'burn':
      return {
        label: 'Burned',
        variant: 'destructive',
        icon: <Flame className="h-3 w-3" />,
        otherParty: '-',
      };
    default:
      return {
        label: entry.transferType,
        variant: 'secondary',
        icon: null,
        otherParty: '-',
      };
  }
}

export function TransferHistoryList({
  transfers,
  currentUserId,
  loading,
  hasMore,
  onLoadMore,
}: TransferHistoryListProps) {
  if (loading && transfers.length === 0) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-10 w-full" />
        ))}
      </div>
    );
  }

  if (transfers.length === 0) {
    return <p className="text-center text-muted-foreground py-8">No transfer history yet.</p>;
  }

  return (
    <>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[160px]">Date</TableHead>
              <TableHead className="w-[100px]">Type</TableHead>
              <TableHead>Item</TableHead>
              <TableHead>Other Party</TableHead>
              <TableHead className="text-right w-[80px]">Qty</TableHead>
              <TableHead className="w-[180px]">Notes</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {transfers.map((entry) => {
              const display = getTransferDisplay(entry, currentUserId);
              return (
                <TableRow key={entry.transferId}>
                  <TableCell className="text-sm text-muted-foreground">
                    {new Date(entry.createdAt).toLocaleString()}
                  </TableCell>
                  <TableCell>
                    <Badge variant={display.variant} className="text-xs gap-1">
                      {display.icon}
                      {display.label}
                    </Badge>
                  </TableCell>
                  <TableCell className="font-medium">{entry.itemName || entry.itemDefId}</TableCell>
                  <TableCell className="font-mono text-xs">{display.otherParty}</TableCell>
                  <TableCell className="text-right">{entry.quantity}</TableCell>
                  <TableCell className="text-sm text-muted-foreground max-w-[180px] truncate">
                    {entry.notes || '-'}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      {hasMore && (
        <div className="text-center mt-4">
          <Button variant="ghost" size="sm" onClick={onLoadMore}>
            Load more
          </Button>
        </div>
      )}
    </>
  );
}
